import Image, { ImageProps } from 'next/image';
import { forwardRef, Ref } from 'react';
import { clsx } from 'clsx';

import { shouldSkipOptimization } from '@monoimin/utils/images';

type WithCommonProps<T> = T &
  Pick<
    ImageProps,
    | 'alt'
    | 'priority'
    | 'quality'
    | 'sizes'
    | 'className'
    | 'unoptimized'
    | 'onClick'
    | 'draggable'
    | 'style'
    | 'placeholder'
    | 'blurDataURL'
  >;

type Patterns = 'fill' | 'dimensions';

export type FillPatternProps = WithCommonProps<{
  pattern: 'fill';
  width?: never;
  height?: never;
  containerClassName?: string;
  src: ImageProps['src'];
  sizes?: Required<ImageProps['sizes']>;
  containerStyle?: ImageProps['style'];
  dataTestId?: string;
}>;

export type DimensionsPatternProps = WithCommonProps<{
  pattern: 'dimensions';
  width: Required<ImageProps['width']>;
  height: Required<ImageProps['height']>;
  containerClassName?: never;
  src: ImageProps['src'];
  containerStyle?: never;
  dataTestId?: string;
}>;

type ComponentRef<T extends Patterns> = T extends 'dimensions'
  ? HTMLImageElement
  : HTMLDivElement;

const setRequiredClassName = (className?: string) => {
  if (className?.includes('absolute') || className?.includes('relative')) {
    return className;
  }

  return `relative ${className}`;
};


export const UniversalImage = forwardRef(function UniversalImage<
  T extends Patterns
>(
  {
    src,
    alt,
    width,
    height,
    sizes,
    containerClassName,
    quality,
    priority,
    pattern,
    className,
    unoptimized,
    onClick,
    draggable,
    style,
    containerStyle,
    placeholder,
    blurDataURL,
    dataTestId = 'universal-image',
  }: FillPatternProps | DimensionsPatternProps,
  ref: Ref<ComponentRef<T>>
) {
  const imageClassName = clsx(`universal-image-${pattern}`, className);
  const shouldUnoptimize = unoptimized || shouldSkipOptimization(src);

  if (pattern === 'fill' && src) {
    return (
      <div
        onClick={onClick}
        ref={ref as Ref<HTMLDivElement>}
        className={clsx(setRequiredClassName(containerClassName))}
        style={containerStyle}
        data-testid={dataTestId}
      >
        <Image
          src={src}
          alt={alt}
          fill
          sizes={sizes}
          priority={priority}
          quality={quality}
          className={imageClassName}
          unoptimized={shouldUnoptimize}
          draggable={draggable}
          style={style}
          placeholder={placeholder}
          blurDataURL={blurDataURL}
        />
      </div>
    );
  }

  if (pattern === 'dimensions' && src) {
    return (
      <Image
        data-testid={dataTestId}
        onClick={onClick}
        ref={ref as Ref<HTMLImageElement>}
        src={src}
        alt={alt}
        width={width}
        height={height}
        sizes={sizes}
        priority={priority}
        quality={quality}
        className={imageClassName}
        unoptimized={shouldUnoptimize}
        draggable={draggable}
        style={style}
        placeholder={placeholder}
        blurDataURL={blurDataURL}
      />
    );
  }

  return null;
});
